import React from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { HelpCircle } from 'react-feather';
import { useTranslations } from 'use-intl';

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
	return (
		<div className="flex items-center gap-2 mb-4">
			<Typography type="text" weight="semibold">
				{title}
			</Typography>
			{hint && (
				<Tooltip content={hint}>
					<HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
				</Tooltip>
			)}
		</div>
	);
}

function ConfigSection({
	title,
	hint,
	children,
	className = ''
}: {
	title: string;
	hint?: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={`rounded-lg border border-gray-200 bg-white p-5 ${className}`}>
			<SectionHeader title={title} hint={hint} />
			{children}
		</div>
	);
}

interface BehaviorMessagesTabProps {
	agentType: string;
	transitionMessage: string;
	transitionMessageWhereItHappens: string | null;
	socialNetworkAnalysisFailureMessage: string;
	isSaving: boolean;
	hasChanges: boolean;
	onTransitionMessageChange: (value: string) => void;
	onSocialNetworkAnalysisFailureMessageChange: (value: string) => void;
	onSave: () => void;
}

export const BehaviorMessagesTab: React.FC<BehaviorMessagesTabProps> = ({
	agentType,
	transitionMessage,
	transitionMessageWhereItHappens,
	socialNetworkAnalysisFailureMessage,
	isSaving,
	hasChanges,
	onTransitionMessageChange,
	onSocialNetworkAnalysisFailureMessageChange,
	onSave
}) => {
	const t = useTranslations('ManageAgents');

	return (
		<div className="w-full space-y-6 mt-4">
			<div className="grid gap-6">
				<ConfigSection title={t('BehaviorMessages')} hint={t('BehaviorMessagesHint')}>
					<div className="space-y-5">
						{transitionMessageWhereItHappens && (
							<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
								<Typography type="text" size="sm" weight="semibold" className="mb-1">
									{t('TransitionMessageWhenSent')}
								</Typography>
								<Typography type="text" size="sm" color="subtitle">
									{transitionMessageWhereItHappens}
								</Typography>
							</div>
						)}
						<div>
							<TextArea
								name="transitionMessage"
								id="transitionMessage"
								label={t('TransitionMessage')}
								hint={t('TransitionMessageSubtitle')}
								value={transitionMessage}
								onChange={(e) => onTransitionMessageChange(e.target.value)}
								disabled={isSaving}
								rows={4}
								placeholder={t('TransitionMessagePlaceholder')}
							/>
						</div>
						{agentType === 'social_network' && (
							<div>
								<TextArea
									name="socialNetworkAnalysisFailureMessage"
									id="socialNetworkAnalysisFailureMessage"
									label={t('SocialNetworkAnalysisFailureMessage')}
									value={socialNetworkAnalysisFailureMessage}
									onChange={(e) => onSocialNetworkAnalysisFailureMessageChange(e.target.value)}
									disabled={isSaving}
									rows={4}
									placeholder={t('SocialNetworkAnalysisFailureMessagePlaceholder')}
								/>
							</div>
						)}
					</div>
				</ConfigSection>

				{hasChanges && (
					<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-center justify-between">
						<Typography type="text" size="sm" color="subtitle">
							{t('UnsavedChanges')}
						</Typography>
						<Button
							onClick={onSave}
							color="primary"
							size="auto"
							label={isSaving ? t('Saving') : t('SaveChanges')}
							disabled={isSaving}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
